import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import AuthShell from "@/components/AuthShell";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please log in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <>
        <Seo title="Reset password" path="/reset-password" />
        <AuthShell title="Invalid link" subtitle="This password reset link is missing or invalid.">
          <Link to="/forgot-password" className="inline-flex w-full items-center justify-center rounded-full bg-foreground px-6 py-3.5 text-sm font-semibold text-background" data-testid="request-new-link-btn">
            Request a new link
          </Link>
        </AuthShell>
      </>
    );
  }

  return (
    <>
      <Seo title="Reset password" description="Set a new password for your StUpEco Workspace account." path="/reset-password" />
      <AuthShell title="Set a new password" subtitle="Choose a strong password to secure your account.">
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div>
            <label className="mb-1.5 block text-sm font-medium">New password</label>
            <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="reset-password-input" placeholder="At least 8 characters" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">Confirm password</label>
            <input required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="reset-confirm-input" placeholder="Re-enter password" />
          </div>
          <button type="submit" disabled={loading} data-testid="reset-submit-btn" className="w-full rounded-full brand-gradient-bg px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60">
            {loading ? "Updating…" : "Update password"}
          </button>
        </form>
      </AuthShell>
    </>
  );
}
